import { useState } from "react";
import { DetailField } from "./DetailField";
import { Select, TextArea } from "./Field";
import { DialogActions, Modal } from "./Modal";
import { STATUS_META, STATUS_ORDER } from "../data/status";
import type { RequestStatus } from "../data/types";

/** The server's bound on a note; the box stops there rather than failing on save. */
const MAX_NOTE = 5_000;

/** The two moves that hand the request to the customer, and what to say. */
const HANDOFF: Partial<Record<RequestStatus, { title: string; help: string; required: boolean }>> = {
  waiting: {
    title: "Move to Waiting on customer?",
    help: "Tell the customer what you need from them. They see it in the portal, and it goes into the next update you send.",
    required: true,
  },
  validation: {
    title: "Move to Validation?",
    help: "Optional: tell the customer what to check and where.",
    required: false,
  },
};

/**
 * The Status select, with a stop on the two moves that hand the request to
 * the customer.
 *
 * A request in Waiting on customer with no note is a request the customer
 * cannot act on, so that move will not save without one; Validation offers
 * the box and lets it go empty. The note and the move travel in one PATCH,
 * so neither can land without the other. The select stays controlled by the
 * saved status, so a cancelled move needs nothing to put back.
 */
export function StatusField({
  value,
  disabled,
  onChange,
}: {
  value: RequestStatus;
  disabled?: boolean;
  /** Saves the move. The dialog waits for it: a failure keeps the note on
   *  screen with the reason, rather than closing and losing what was typed. */
  onChange: (status: RequestStatus, customerNote?: string) => Promise<void> | void;
}) {
  const [pending, setPending] = useState<RequestStatus | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ask = pending ? HANDOFF[pending] : undefined;
  const missing = ask?.required === true && note.trim() === "";
  const close = () => {
    setPending(null);
    setNote("");
    setError(null);
  };

  return (
    <DetailField label="Status">
      <Select
        aria-label="Status"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value as RequestStatus;
          if (next === value) return;
          if (HANDOFF[next]) setPending(next);
          else onChange(next);
        }}
      >
        {STATUS_ORDER.map((s) => (
          <option key={s} value={s}>
            {STATUS_META[s].label}
          </option>
        ))}
      </Select>

      <Modal
        open={pending !== null}
        onClose={close}
        title={ask?.title ?? ""}
        footer={
          <DialogActions
            onCancel={close}
            onConfirm={async () => {
              if (!pending || missing || busy) return;
              setBusy(true);
              setError(null);
              try {
                await onChange(pending, note.trim() || undefined);
                close();
              } catch (e) {
                setError(e instanceof Response ? await e.text() : e instanceof Error ? e.message : "Could not move the request");
              } finally {
                setBusy(false);
              }
            }}
            confirmLabel={busy ? "Moving…" : "Move request"}
            confirmDisabled={missing || busy}
          />
        }
      >
        <p className="text-sm text-[var(--text-2)]">{ask?.help}</p>
        <div className="mt-3">
          <TextArea
            aria-label="Note to the customer"
            value={note}
            maxLength={MAX_NOTE}
            onChange={(e) => setNote(e.target.value)}
            placeholder={ask?.required ? "What do you need from them?" : "What should they check?"}
          />
          {note.length > MAX_NOTE - 500 && (
            <p className="mt-1 text-xs text-[var(--text-2)]">
              {note.length.toLocaleString("en-US")} / {MAX_NOTE.toLocaleString("en-US")}
            </p>
          )}
        </div>
        {error && (
          <p role="alert" className="mt-3 text-sm text-error-600">
            {error}
          </p>
        )}
      </Modal>
    </DetailField>
  );
}
